import { useBriefing } from "@/context/BriefingContext";
import Reveal from "@/components/Reveal";
import ImagePlate from "@/components/ImagePlate";
import VocabTag from "@/components/VocabTag";
import { GIGA_IMAGES } from "@/lib/images";

export default function Hero() {
  const { openBriefing } = useBriefing();

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative border-b border-[color:var(--g1-rule)]"
    >
      <div className="max-w-[1500px] mx-auto px-5 md:px-10 lg:px-14">
        {/* Meta line */}
        <div className="grid grid-cols-12 gap-4 pt-24 md:pt-28 lg:pt-32 items-baseline">
          <div className="col-span-6 md:col-span-4 g1-label text-[color:var(--g1-ink-2)]">
            SEC. 00 / CATEGORY
          </div>
          <div className="hidden md:flex md:col-span-4 g1-label justify-center text-[color:var(--g1-ink-2)] items-center gap-3">
            <VocabTag kind="FUSED" />
          </div>
          <div className="col-span-6 md:col-span-4 g1-label text-right text-[color:var(--g1-ink-2)]">
            {/* cleared */}
          </div>
        </div>

        <div className="h-px bg-[color:var(--g1-rule)] mt-4" />

        {/* Hero image plate */}
        <div className="pt-8 md:pt-10">
          <Reveal>
            <ImagePlate
              src={GIGA_IMAGES.heroCleanroom}
              alt="Autonomous pharmaceutical cleanroom corridor"
              aspect="21/9"
              cornersLight
              loading="eager"
              className="md:aspect-[21/9]"
            />
          </Reveal>
        </div>

        {/* Headline under plate — commanding, compressed */}
        <div className="grid grid-cols-12 gap-4 md:gap-8 pt-10 md:pt-16 pb-12 md:pb-20">
          <div className="col-span-12 lg:col-span-10">
            <Reveal delay={80}>
              <h1
                data-testid="hero-headline"
                className="g1-display text-[36px] sm:text-[52px] md:text-[84px] lg:text-[108px] xl:text-[124px]"
              >
                The autonomous <br className="hidden md:inline" />
                <span className="g1-display-italic">pharmaceutical</span>{" "}
                factory era has arrived.
              </h1>
            </Reveal>
          </div>
        </div>

        {/* Support row — sparse */}
        <div className="grid grid-cols-12 gap-4 md:gap-8 border-t border-[color:var(--g1-rule)] pt-8 md:pt-12 pb-14 md:pb-20">
          <div className="col-span-12 md:col-span-5 lg:col-span-4">
            <Reveal delay={140}>
              <p className="g1-lede" data-testid="hero-lede">
                Giga One integrates industrial AGI with a production-ready
                pharmaceutical manufacturing body — onshored in the United
                States.
              </p>
            </Reveal>
          </div>

          <div className="col-span-12 md:col-span-3 lg:col-span-3 lg:col-start-7">
            <Reveal delay={200}>
              <dl className="flex flex-col gap-4">
                <div className="flex justify-between border-b border-[color:var(--g1-rule)] pb-2">
                  <dt className="g1-label">FOCUS</dt>
                  <dd className="font-mono text-[12px] uppercase tracking-[0.12em] text-[color:var(--g1-ink)]">
                    Industrial AGI
                  </dd>
                </div>
                <div className="flex justify-between border-b border-[color:var(--g1-rule)] pb-2">
                  <dt className="g1-label">GEO</dt>
                  <dd className="font-mono text-[12px] uppercase tracking-[0.12em] text-[color:var(--g1-ink)]">
                    United States
                  </dd>
                </div>
                <div className="flex justify-between border-b border-[color:var(--g1-rule)] pb-2">
                  <dt className="g1-label">CLASS</dt>
                  <dd className="font-mono text-[12px] uppercase tracking-[0.12em] text-[color:var(--g1-ink)]">
                    Strategic Infrastructure
                  </dd>
                </div>
              </dl>
            </Reveal>
          </div>

          <div className="col-span-12 md:col-span-4 lg:col-span-3 lg:col-start-10 flex md:justify-end items-start">
            <Reveal delay={260}>
              <div className="flex flex-col gap-3 w-full md:w-auto">
                <button
                  type="button"
                  data-testid="hero-request-briefing"
                  onClick={openBriefing}
                  className="g1-btn g1-btn-primary"
                >
                  Request Briefing
                  <span className="g1-btn-arrow" aria-hidden>
                    →
                  </span>
                </button>
                <a
                  href="#thesis"
                  data-testid="hero-read-thesis"
                  className="g1-btn g1-btn-ghost"
                >
                  Read the Thesis
                  <span className="g1-btn-arrow" aria-hidden>
                    ↓
                  </span>
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </div>

      {/* Ticker */}
      <div className="border-t border-[color:var(--g1-rule)] bg-[color:var(--g1-paper)]/60">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 lg:px-14 py-3 flex items-center justify-between gap-6 overflow-hidden">
          <span className="g1-ticker g1-ticker-strong whitespace-nowrap">
            · INDUSTRIAL AGI
          </span>
          <span className="g1-ticker g1-ticker-strong whitespace-nowrap hidden sm:inline">
            · ZERO HALLUCINATION
          </span>
          <span className="g1-ticker g1-ticker-strong whitespace-nowrap hidden md:inline">
            · CLOSED LOOP
          </span>
          <span className="g1-ticker g1-ticker-strong whitespace-nowrap hidden md:inline">
            · ONSHORED
          </span>
          <span className="g1-ticker g1-ticker-strong whitespace-nowrap hidden lg:inline">
            · AGI + PRODUCTION
          </span>
        </div>
      </div>
    </section>
  );
}
